/**
 *
 * AddRepository -> GitLab, Github
 *
 */

import React, { useEffect, useMemo, useReducer, useState } from 'react';

import { CloudProviders, RepositoryService } from '@ariksa/cloud-account/api';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  Center,
  Stack,
  Flex,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ConnectProvider,
  getRepositoryConnectOptions,
} from 'api/onboarding/repositoryConnect';
import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import { ButtonGroup, FormAction, PrimaryButton } from 'components/DataEntry';
import { defaultStyles, Form } from 'components/DataEntry/Form';
import { FormStyles } from 'components/DataEntry/Form/types';
import { CheckmarkIcon, QuestionCircleIcon } from 'components/Icons';
import { actions as appActions } from 'containers/App/slice';
import { BitbucketTokenHelp } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/Help/Bitbucket/BitbucketGetTokenHelp';
import { GitHubTokenHelp } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/Help/GitHub/GitHubGetTokenHelp';
import { GitLabTokenHelp } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/Help/GitLab/GitLabGetTokenHelp';
import { RepositoryAutoConnect } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/RepositoryAutoConnect';
import { getRepositoryFormStyles } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/utils';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';

const AUTO_CONNECT_PROVIDERS: Partial<Record<string, ConnectProvider>> = {
  [CloudProviders.Gitlab]: 'gitlab',
  [CloudProviders.Github]: 'github',
};

const MODE_OPTIONS = [
  { label: 'Connect automatically', value: 'auto' },
  { label: 'Use a token', value: 'token' },
];

interface Props {
  onCancel();
  onSuccess?();
  getAccounts?();
  selectedProvider: string;
  actionType: FormAction;
  accountDetails: Record<string, any>;
  styles?: FormStyles;
}

export const AddRepositoryForm = (props: Props) => {
  const {
    onCancel,
    onSuccess,
    getAccounts,
    selectedProvider,
    actionType,
    accountDetails,
    styles,
  } = props;
  const dispatch = useDispatch();
  const { validateToken, onboardAccount } = useSelector(selectSetup);
  const [isEdit] = useState(actionType === 'Update');
  const connectProvider = AUTO_CONNECT_PROVIDERS[selectedProvider];
  const [autoConnectAvailable, setAutoConnectAvailable] = useState(false);
  const [mode, setMode] = useState<'auto' | 'token'>('auto');
  // Same fixed form width for both modes; help panel fills the rest of the row.
  const formStyles = useMemo(() => getRepositoryFormStyles(styles), [styles]);

  useEffect(() => {
    if (isEdit || !connectProvider) return;
    let active = true;
    getRepositoryConnectOptions()
      .then(
        options =>
          active && setAutoConnectAvailable(!!options[connectProvider]),
      )
      .catch(() => active && setAutoConnectAvailable(false));
    return () => {
      active = false;
    };
  }, [isEdit, connectProvider]);

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      isHelpOpen: false,
      isValid: false,
      validateTokenRequest: false,
      disableValidateButton: false,
      token: '',
      accountNameError: '',
      name: '',
      username: '',
    },
  );

  const toggleIsHelpOpen = () => updateState({ isHelpOpen: !state.isHelpOpen });

  useEffect(() => {
    if (state.validateTokenRequest)
      updateState({ isValid: validateToken.data });
  }, [validateToken.data, state.validateTokenRequest]);

  useEffect(() => {
    isEdit &&
      updateState({
        name: accountDetails?.name,
        token:
          accountDetails?.gitlab?.access_token ??
          accountDetails?.github?.access_token ??
          accountDetails?.bitbucket?.application_password,
        username: accountDetails?.bitbucket?.username,
        isValid: true,
        disableValidateButton: true,
      });
  }, [isEdit, accountDetails]);

  const handleReset = () => {
    onCancel();
  };

  const handleSuccess = () => {
    if (onSuccess) onSuccess();
    else onCancel();

    getAccounts?.();
    dispatch(appActions.loadEnvAndAccounts());
  };

  const handleSubmit = data => {
    const repo: Record<string, any> =
      selectedProvider === CloudProviders.Gitlab
        ? { gitlab: { access_token: state.token } }
        : selectedProvider === CloudProviders.Github
        ? { github: { access_token: state.token } }
        : {
            bitbucket: {
              username: state.username,
              application_password: state.token,
            },
          };

    if (isEdit)
      dispatch(
        setupActions.updateCloudAccount({
          q: {
            uuid: accountDetails.uuid,
            cloudAccountUpdate: {
              name: state.name,
              cloud_type: selectedProvider as CloudProviders,
              ...repo,
            },
          },
          onSuccess: handleSuccess,
        }),
      );
    else
      dispatch(
        setupActions.onboardAccount({
          q: {
            cloudAccountCreateRequest: {
              cloud_type: selectedProvider as CloudProviders,
              name: state.name,
              ...repo,
            },
          },
          onSuccess: handleSuccess,
        }),
      );
  };

  const onClickValidate = () => {
    updateState({ disableValidateButton: true, validateTokenRequest: true });
    const repoType =
      selectedProvider === CloudProviders.Gitlab
        ? RepositoryService.Gitlab
        : selectedProvider === CloudProviders.Github
        ? RepositoryService.Github
        : RepositoryService.Bitbucket;
    const repo =
      selectedProvider === CloudProviders.Gitlab
        ? { gitlab: { access_token: state.token } }
        : selectedProvider === CloudProviders.Github
        ? { github: { access_token: state.token } }
        : {
            bitbucket: {
              username: state.username,
              application_password: state.token,
            },
          };
    dispatch(
      setupActions.validateToken({
        q: {
          repositoryRequest: {
            service_type: repoType,
            ...repo,
          },
        },
      }),
    );
  };

  const showModeSwitch = !isEdit && !!connectProvider && autoConnectAvailable;

  return (
    <Stack spacing={4} w="full">
      {showModeSwitch && (
        // Use the shared segmented ButtonGroup for the auto/token mode switch.
        <ButtonGroup
          items={MODE_OPTIONS}
          value={MODE_OPTIONS.find(option => option.value === mode)}
          onClick={option => setMode(option.value)}
        />
      )}
      {showModeSwitch && mode === 'auto' ? (
        // Pass cancel + styles so the Form-based auto-connect view matches the token form.
        <RepositoryAutoConnect
          provider={connectProvider!}
          onCancel={handleReset}
          styles={formStyles}
        />
      ) : (
        <Form
          schema={{
            name: {
              type: 'text',
              label: 'Name',
              isRequired: true,
              tooltip: 'Only Alphanumeric, hyphen is allowed.',
              error: state.accountNameError,
              onChange: value => {
                updateState({ name: value });
                if (value.match(/^[a-zA-Z0-9-]*$/i))
                  updateState({ accountNameError: '' });
                else
                  updateState({
                    accountNameError: 'Only Alphanumeric, hyphen is allowed!',
                  });
              },
              value: state.name,
              autoFocus: true,
            },
            ...(selectedProvider === CloudProviders.Bitbucket && !isEdit
              ? {
                  username: {
                    type: 'text',
                    label: 'Username',
                    isRequired: true,
                    onChange: value =>
                      updateState({
                        username: value,
                        isValid: false,
                        disableValidateButton: false,
                      }),
                    value: state.username,
                  },
                }
              : {}),
            token: {
              type: 'custom-with-form-control',
              label:
                selectedProvider === CloudProviders.Bitbucket
                  ? 'Application password'
                  : 'API Key or Token',
              placeholder:
                selectedProvider === CloudProviders.Bitbucket
                  ? 'Enter app password'
                  : 'Enter API Key or Token',
              isRequired: true,
              component: (
                <Stack w="full" pb={4}>
                  <InputGroup>
                    <Input
                      {...defaultStyles?.textField?.input}
                      isDisabled={isEdit}
                      type="password"
                      value={
                        isEdit ? '******************************' : state.token
                      }
                      onChange={e => {
                        updateState({
                          isValid: false,
                          validateTokenRequest: false,
                          disableValidateButton: false,
                          token: e.target.value,
                        });
                      }}
                    />
                    {!!state.isValid && (
                      <InputRightElement w={8} h={8} p={2}>
                        <WithSpinner
                          loadStatus={{ loading: validateToken.isLoading }}
                        >
                          <Center
                            borderRadius="full"
                            bg="green.300"
                            color="white"
                            p={0.5}
                          >
                            <CheckmarkIcon />
                          </Center>
                        </WithSpinner>
                      </InputRightElement>
                    )}
                  </InputGroup>
                  {!state.isValid &&
                    state.validateTokenRequest &&
                    !validateToken.isLoading && (
                      <Box w="full">
                        <Alert
                          status="error"
                          borderRadius={6}
                          title={'Please enter valid token!'}
                        >
                          <AlertIcon />
                          {'Please enter valid token!'}
                        </Alert>
                      </Box>
                    )}
                  <Flex justify="flex-end" w="full">
                    <PrimaryButton
                      onClick={onClickValidate}
                      isDisabled={
                        state.disableValidateButton ||
                        (selectedProvider === CloudProviders.Bitbucket &&
                          !state.username) ||
                        !state.token
                      }
                      isLoading={validateToken.isLoading}
                    >
                      VALIDATE
                    </PrimaryButton>
                  </Flex>
                </Stack>
              ),
              helpIcon: (
                <Box
                  onClick={toggleIsHelpOpen}
                  _hover={{ cursor: 'pointer' }}
                  color="primary"
                >
                  <QuestionCircleIcon />
                </Box>
              ),
              isHelpOpen: state.isHelpOpen,
              helpComponent:
                selectedProvider === CloudProviders.Gitlab ? (
                  <GitLabTokenHelp
                    onClose={() => updateState({ isHelpOpen: false })}
                  />
                ) : selectedProvider === CloudProviders.Github ? (
                  <GitHubTokenHelp
                    onClose={() => updateState({ isHelpOpen: false })}
                  />
                ) : (
                  <BitbucketTokenHelp
                    onClose={() => updateState({ isHelpOpen: false })}
                  />
                ),
            },
            note: {
              type: 'custom',
              component: () => (
                <Box color="primary">
                  Note: Ariksa will automatically populate details of the
                  repository or project associated with the API key or token
                  provided
                </Box>
              ),
            },
          }}
          styles={formStyles}
          showHelp
          buttonOptions={{
            submit: {
              name: 'OK',
              isLoading: onboardAccount.isLoading,
              isDisabled:
                !state.isValid ||
                !state.name ||
                (selectedProvider === CloudProviders.Bitbucket &&
                  !state.username),
            },
            reset: {
              name: 'CANCEL',
              isVisible: true,
              onClick: handleReset,
            },
          }}
          handleSubmit={handleSubmit}
        />
      )}
    </Stack>
  );
};
